import { Button, Timeline, Tag, Space, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { IVideo } from "../interfaces";
import { recordApi, retryVideo } from "../api";
import { message } from "antd";
import {
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
  CloudUploadOutlined,
  VideoCameraOutlined,
  ReloadOutlined,
  PlayCircleFilled,
  RedoOutlined,
} from "@ant-design/icons";
import useProgress from "../hooks/useProgress";

const STATUS_CONFIG = {
  success: {
    color: "#0ea5e9",
    bg: "#f0f9ff",
    border: "#bae6fd",
    icon: <CheckCircleFilled />,
  },
  failed: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    icon: <CloseCircleFilled />,
  },
  active: {
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fcd34d",
    icon: <LoadingOutlined />,
  },
  pending: {
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#e5e7eb",
    icon: <ClockCircleFilled />,
  },
};

const TASK_CONFIG = {
  rendring: {
    title: "HLS Streaming",
    description: "Adaptive bitrate streaming",
    icon: <CloudUploadOutlined />,
    messages: {
      active: "Uploading HLS segments...",
      pending: "Waiting to upload HLS video",
      failed: "HLS upload failed",
      success: "HLS streaming ready",
    },
  },
  downloadRendring: {
    title: "MP4 Download",
    description: "Standard video format",
    icon: <VideoCameraOutlined />,
    messages: {
      active: "Uploading MP4 file...",
      pending: "Waiting to upload MP4",
      failed: "MP4 upload failed",
      success: "MP4 download ready",
    },
  },
};

function VideoProgress({ video, stream_key }: { video: IVideo, stream_key: string }) {
  const [timelines, setTimelines] = useState<any>(null);
  const { hlsProgress, mp4Progress, hlsStatus, mp4Status } = useProgress(stream_key);
  const [retryingHls, setRetryingHls] = useState(false);
  const [retryingMp4, setRetryingMp4] = useState(false);

  const handleResumeVideo = async (taskType: IVideo["tasks"][0]["type"]) => {
    await recordApi.post(`/resume/${video._id}`, { taskType });
  };

  const handleRetryHls = async () => {
    setRetryingHls(true);
    try {
      await retryVideo(stream_key, 'hls');
      message.success('HLS retry initiated');
    } catch (e: any) {
      message.error(e.message || 'Failed to retry HLS');
    } finally {
      setRetryingHls(false);
    }
  };

  const handleRetryMp4 = async () => {
    setRetryingMp4(true);
    try {
      await retryVideo(stream_key, '360p878kbs');
      message.success('MP4 retry initiated');
    } catch (e: any) {
      message.error(e.message || 'Failed to retry MP4');
    } finally {
      setRetryingMp4(false);
    }
  };

  // Get task status - prioritize SSE status over video.tasks status
  const getTaskStatus = (taskType: string): "success" | "pending" | "failed" | "active" => {
    // Use SSE status if available (not pending), otherwise fall back to video.tasks
    const sseStatus = taskType === 'rendring' ? hlsStatus : mp4Status;
    if (sseStatus !== 'pending') {
      return sseStatus;
    }

    // Fall back to video.tasks status
    const task = video.tasks.find(t => t.type === taskType);
    return task?.status || 'pending';
  };

  useEffect(() => {
    const tasks = video.tasks;
    if (tasks.length === 0 || !['rendering', 'rendered', 'failed'].includes(video.status)) return;

    const hlsTask = tasks.find(t => t.type === 'rendring');
    const mp4Task = tasks.find(t => t.type === 'downloadRendring');
    const filteredTasks = [hlsTask, mp4Task].filter(Boolean);

    setTimelines(
      filteredTasks.map((task) => {
        const config = TASK_CONFIG[task.type];
        // Use SSE-updated status
        const currentStatus = getTaskStatus(task.type);
        const statusConfig = STATUS_CONFIG[currentStatus];
        const progress = task.type === "rendring" ? hlsProgress : mp4Progress;

        return {
          dot: (
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: statusConfig.bg,
                border: `2px solid ${statusConfig.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: statusConfig.color,
                fontSize: 14,
              }}
            >
              {config.icon}
            </div>
          ),
          children: (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "16px 20px",
                borderRadius: 12,
                background: statusConfig.bg,
                border: `1px solid ${statusConfig.border}`,
                boxShadow: currentStatus === "active" ? "0 2px 8px rgba(14, 165, 233, 0.1)" : "none",
                transition: "all 0.3s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#1f2937",
                    }}
                  >
                    {config.title}
                  </span>
                  <Tag
                    style={{
                      margin: 0,
                      border: "none",
                      background: "rgba(255,255,255,0.6)",
                      color: statusConfig.color,
                      fontSize: 12,
                      fontWeight: 500,
                      padding: "2px 10px",
                      borderRadius: 20,
                    }}
                  >
                    <Space size={4}>
                      {statusConfig.icon}
                      {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
                    </Space>
                  </Tag>
                </div>

                <Space>
                  {currentStatus === "failed" && (
                    <Button
                      onClick={() => handleResumeVideo(task.type)}
                      size="small"
                      type="primary"
                      icon={<ReloadOutlined />}
                      style={{
                        borderRadius: 6,
                        fontWeight: 500,
                      }}
                    >
                      Retry
                    </Button>
                  )}
                  <Tooltip title={`Retry ${task.type === 'rendring' ? 'HLS' : 'MP4'} processing`}>
                    <Button
                      onClick={task.type === 'rendring' ? handleRetryHls : handleRetryMp4}
                      size="small"
                      type="default"
                      icon={<RedoOutlined />}
                      loading={task.type === 'rendring' ? retryingHls : retryingMp4}
                      style={{
                        borderRadius: 6,
                        fontWeight: 500,
                      }}
                    />
                  </Tooltip>
                </Space>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {config.messages[currentStatus]}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: statusConfig.color,
                    }}
                  >
                    {progress}%
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: "rgba(0,0,0,0.06)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progress}%`,
                      background: statusConfig.color,
                      borderRadius: 3,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            </div>
          ),
        };
      })
    );
  }, [video.tasks, video.status, hlsProgress, mp4Progress, hlsStatus, mp4Status]);

  if (!["rendering", "rendered"].includes(video.status)) {
    const isLiveOrSchedule = ["live", "schedule"].includes(video.status);
    return (
      <div
        style={{
          // marginTop: 16,
          padding: "18px 20px",
          borderRadius: 12,
          background: isLiveOrSchedule
            ? "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)"
            : "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
          border: isLiveOrSchedule ? "1px solid #d1d5db" : "1px solid #bae6fd",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isLiveOrSchedule ? "#6b7280" : "#0ea5e9",
            fontSize: 20,
            boxShadow: isLiveOrSchedule
              ? "0 2px 8px rgba(107, 114, 128, 0.15)"
              : "0 2px 8px rgba(14, 165, 233, 0.15)",
          }}
        >
          <PlayCircleFilled />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: isLiveOrSchedule ? "#4b5563" : "#0369a1",
            }}
          >
            {isLiveOrSchedule ? "Not Available" : "Ready to Render"}
          </span>
          <span style={{ fontSize: 13, color: isLiveOrSchedule ? "#6b7280" : "#0ea5e9" }}>
            {isLiveOrSchedule
              ? "Rendering will be available after the live stream ends."
              : "Rendering will begin shortly. You'll see progress updates here."}
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          padding: "0 4px",
        }}
      >
        {/* <CloudUploadOutlined style={{ color: "#0ea5e9", fontSize: 16 }} />
        <span style={{ fontSize: 20, fontWeight: 600, color: "#374151" }}>
          Video Processing
        </span> */}
      </div>
      <Timeline
        style={{ paddingLeft: 4 }}
        items={timelines}
      />
    </div>
  );
}

export default VideoProgress;
